
import { useEffect, useRef, useState, useCallback } from 'react'
import { useJobStore } from '@/stores/jobStore'
import { useCanvasBridgeStore } from '@/stores/useCanvasBridgeStore'
import { apiCall } from '@/lib/api/api-helpers'
import { toast } from '@/components/ui/toast'
import type { JobType } from '@genieartinc/schemas'

/**
 * WebSocket event types matching backend EventBusPort EVENT_TYPES
 */
const WS_EVENTS = {
  GENERATION_START: 'ai:generation:start',
  GENERATION_PROGRESS: 'ai:generation:progress',
  GENERATION_COMPLETE: 'ai:generation:complete',
  GENERATION_ERROR: 'ai:generation:error',
} as const

interface WSGenerationStartPayload {
  jobId: string
  nodeId: string
  type: string
  status: 'RUNNING'
}

interface WSGenerationProgressPayload {
  jobId: string
  nodeId: string
  progress: number
  message?: string
}

interface WSGenerationCompletePayload {
  jobId: string
  nodeId: string
  output: unknown
  executionTime: number
}

interface WSGenerationErrorPayload {
  jobId: string
  nodeId: string
  error: string
  retryCount: number
  willRetry: boolean
}

type ConnectionStatus = 'disconnected' | 'connecting' | 'connected' | 'error'

// Debounce low balance toasts — show at most once per 60s
let lastLowBalanceToastAt = 0
const LOW_BALANCE_TOAST_COOLDOWN_MS = 60_000

/**
 * Check the shared wallet state and show a non-intrusive toast if balance is low.
 * Called after job completion so users are aware before starting another generation.
 */
function showLowBalanceToastIfNeeded() {
  // Import the shared state getter to avoid circular dependency issues
  // The wallet poller updates this synchronously, and we just dispatched
  // genie:balance-changed, so give it a moment to refresh.
  setTimeout(() => {
    try {
      // Read the current shared wallet state (populated by useWalletBalance poller)
      // We use a dynamic import-free approach: read from the DOM event we just dispatched
      // Instead, we fetch the balance directly for an accurate post-deduction read
      const now = Date.now()
      if (now - lastLowBalanceToastAt < LOW_BALANCE_TOAST_COOLDOWN_MS) return

      // Access the shared state directly — the useWalletBalance module exposes it
      // via the singleton pattern. We'll use the wallet API for a fresh check.
      void import('@/lib/api/wallet').then(({ walletAPI }) => {
        void walletAPI.getBalance(true).then((result) => {
          if (!result.lowBalance) return
          const balance = parseFloat(result.balance)
          const now2 = Date.now()
          if (now2 - lastLowBalanceToastAt < LOW_BALANCE_TOAST_COOLDOWN_MS) return
          lastLowBalanceToastAt = now2

          if (balance <= 0) {
            toast('No credits remaining. Top up to keep creating.', { icon: '⚠️', duration: 6000 })
          } else {
            toast(
              `Balance running low ($${balance.toFixed(2)} remaining). Top up to keep creating.`,
              { icon: '⚠️', duration: 5000 },
            )
          }
        })
      })
    } catch {
      // Silent — toast is non-critical
    }
  }, 1500)
}

/**
 * Canvas WebSocket hook for real-time job updates.
 *
 * Uses native WebSocket (not Socket.IO) to avoid additional dependencies.
 * Connects to the api-app WebSocket server and subscribes to canvas events.
 * Updates the job store on each incoming event.
 *
 * NOTE: Socket.IO client is NOT in web-app dependencies. This hook uses
 * native WebSocket with a simple JSON protocol. When the backend WebSocket
 * server is ready, update the URL and protocol as needed.
 */
export function useCanvasWebSocket(canvasId: string | null, transport: 'websocket' | 'poll' = 'websocket') {
  const [status, setStatus] = useState<ConnectionStatus>('disconnected')
  const wsRef = useRef<WebSocket | null>(null)
  const reconnectTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  const reconnectAttemptsRef = useRef(0)

  const addJob = useJobStore((s) => s.addJob)
  const updateJobStatus = useJobStore((s) => s.updateJobStatus)
  const updateJobProgress = useJobStore((s) => s.updateJobProgress)

  // Use ref for message handler to avoid recreating the connect callback
  // when store selectors change (which would close the WS before it connects)
  const handleMessageRef = useRef<(event: MessageEvent) => void>(() => {})

  handleMessageRef.current = useCallback(
    (event: MessageEvent) => {
      try {
        const data = JSON.parse(event.data as string) as {
          type: string
          payload: unknown
        }

        switch (data.type) {
          case WS_EVENTS.GENERATION_START: {
            const payload = data.payload as WSGenerationStartPayload
            addJob({
              id: payload.jobId,
              type: payload.type as JobType,
              status: 'RUNNING',
              nodeId: payload.nodeId,
              canvasId: canvasId || '',
              progress: 0,
              createdAt: new Date().toISOString(),
            })
            break
          }

          case WS_EVENTS.GENERATION_PROGRESS: {
            const payload = data.payload as WSGenerationProgressPayload
            updateJobProgress(payload.jobId, payload.progress, payload.message)

            // Forward real progress to placeholder nodes
            const progressStore = useJobStore.getState()
            const progressMapping = progressStore.getPlaceholders(payload.jobId)
            if (progressMapping) {
              const progressBridge = useCanvasBridgeStore.getState()
              progressMapping.nodeIds.forEach((placeholderNodeId) => {
                progressBridge.updateNodeStateCallback?.(placeholderNodeId, {
                  generationProgress: payload.progress,
                  generationMessage: payload.message,
                })
              })
            }
            break
          }

          case WS_EVENTS.GENERATION_COMPLETE: {
            const payload = data.payload as WSGenerationCompletePayload
            updateJobStatus(payload.jobId, 'COMPLETED', {
              output: payload.output,
              executionTime: payload.executionTime,
              progress: 100,
            })

            // Update placeholder nodes with processed file data
            const completeStore = useJobStore.getState()
            const completeMapping = completeStore.getPlaceholders(payload.jobId)
            if (completeMapping) {
              const completeBridge = useCanvasBridgeStore.getState()
              const output = payload.output as {
                processedFiles?: Array<{
                  variantId: string
                  uuid: string
                  publicUrl: string
                  filename: string
                  size: number
                  contentType: string
                  width: number
                  height: number
                  versions: Record<string, { url: string; width?: number; height?: number; size?: number }>
                }>
              }

              const processedFiles = output?.processedFiles ?? []

              // Fallback: if processedFiles is empty but variants have imageUrls/videoUrls, use raw URLs
              const variants = (payload.output as { variants?: Array<{ imageUrl?: string; videoUrl?: string; id?: string }> })?.variants ?? []

              completeMapping.nodeIds.forEach((placeholderNodeId, index) => {
                const file = processedFiles[index]
                if (file) {
                  completeBridge.updateNodeStateCallback?.(placeholderNodeId, {
                    generationStatus: 'complete',
                    generationProgress: 100,
                    fileMetadata: {
                      publicUrl: file.versions?.preview?.url || file.versions?.original?.url || file.publicUrl,
                      filename: file.filename,
                      size: file.size,
                      contentType: file.contentType,
                      width: file.width,
                      height: file.height,
                      versions: file.versions,
                    },
                  })
                } else if (variants[index]?.imageUrl || variants[index]?.videoUrl) {
                  // Raw fal.ai URL fallback — media generated but not yet processed to GCS
                  const url = variants[index].imageUrl || variants[index].videoUrl!
                  const isVideo = !!variants[index].videoUrl
                  completeBridge.updateNodeStateCallback?.(placeholderNodeId, {
                    generationStatus: 'complete',
                    generationProgress: 100,
                    fileMetadata: {
                      publicUrl: url,
                      filename: `ai-generated-${variants[index].id || index}.${isVideo ? 'mp4' : 'png'}`,
                      size: 0,
                      contentType: isVideo ? 'video/mp4' : 'image/png',
                      width: 0,
                      height: 0,
                    },
                  })
                } else {
                  // No file and no variant URL — mark as error
                  completeBridge.updateNodeStateCallback?.(placeholderNodeId, {
                    generationStatus: 'error',
                    generationProgress: 0,
                  })
                }
              })

              // Unlock parent node
              completeBridge.updateNodeStateCallback?.(completeMapping.parentNodeId, { locked: false })

              // Dispatch events
              if (processedFiles.length > 0) {
                window.dispatchEvent(new CustomEvent('genie:file-created'))
              }
              window.dispatchEvent(new CustomEvent('genie:balance-changed'))

              // Show low balance toast after generation completes
              showLowBalanceToastIfNeeded()

              // Auto-remove any error placeholders (no processedFile AND no variant URL) after 5s
              const errorPlaceholders = completeMapping.nodeIds.filter((_, i) => !processedFiles[i] && !variants[i]?.imageUrl && !variants[i]?.videoUrl)
              if (errorPlaceholders.length > 0) {
                setTimeout(() => {
                  window.dispatchEvent(new CustomEvent('genie:remove-nodes', {
                    detail: { nodeIds: errorPlaceholders },
                  }))
                }, 5000)
              }

              completeStore.removePlaceholders(payload.jobId)
            }
            break
          }

          case WS_EVENTS.GENERATION_ERROR: {
            const payload = data.payload as WSGenerationErrorPayload
            updateJobStatus(payload.jobId, 'FAILED', {
              error: payload.error,
            })

            // Mark placeholder nodes as errored and schedule removal
            const errorStore = useJobStore.getState()
            const errorMapping = errorStore.getPlaceholders(payload.jobId)
            if (errorMapping) {
              const errorBridge = useCanvasBridgeStore.getState()

              errorMapping.nodeIds.forEach((placeholderNodeId) => {
                errorBridge.updateNodeStateCallback?.(placeholderNodeId, {
                  generationStatus: 'error',
                  generationProgress: 0,
                })
              })

              // Unlock parent node
              errorBridge.updateNodeStateCallback?.(errorMapping.parentNodeId, { locked: false })

              // Auto-remove error placeholders after 5s
              const nodeIdsToRemove = [...errorMapping.nodeIds]
              setTimeout(() => {
                window.dispatchEvent(new CustomEvent('genie:remove-nodes', {
                  detail: { nodeIds: nodeIdsToRemove },
                }))
              }, 5000)

              errorStore.removePlaceholders(payload.jobId)
            }
            break
          }
        }
      } catch {
        // Ignore malformed messages
      }
    },
    [canvasId, addJob, updateJobStatus, updateJobProgress]
  )

  const connect = useCallback(() => {
    if (!canvasId) return

    const apiBaseUrl = import.meta.env.VITE_API_BASE_URL
    if (!apiBaseUrl) return

    // Convert HTTP URL to WS URL
    const wsUrl = apiBaseUrl.replace(/^http/, 'ws')
    const url = `${wsUrl}/ws/canvas/${canvasId}`

    const MAX_RECONNECT_ATTEMPTS = 3

    if (reconnectAttemptsRef.current >= MAX_RECONNECT_ATTEMPTS) {
      // Silently give up after max attempts — WS likely not available
      setStatus('disconnected')
      return
    }

    setStatus('connecting')

    try {
      const ws = new WebSocket(url)
      wsRef.current = ws

      ws.onopen = () => {
        setStatus('connected')
        const wasReconnect = reconnectAttemptsRef.current > 0
        reconnectAttemptsRef.current = 0

        // Join canvas room
        ws.send(JSON.stringify({ type: 'join', room: `canvas:${canvasId}` }))

        // On reconnect, sync any jobs that completed while disconnected
        if (wasReconnect) {
          syncJobsAfterReconnect(canvasId).catch(() => {})
        }
      }

      ws.onmessage = (event) => handleMessageRef.current(event)

      ws.onclose = () => {
        setStatus('disconnected')
        wsRef.current = null

        // Exponential backoff reconnect (max 30s, limited attempts)
        const attempts = reconnectAttemptsRef.current
        if (attempts >= MAX_RECONNECT_ATTEMPTS) return

        const delay = Math.min(1000 * Math.pow(2, attempts), 30000)
        reconnectAttemptsRef.current = attempts + 1

        reconnectTimerRef.current = setTimeout(connect, delay)
      }

      ws.onerror = () => {
        setStatus('error')
        // onclose will fire after onerror, triggering reconnect
      }
    } catch {
      setStatus('error')
    }
  }, [canvasId])

  // Track whether this effect instance is the active one.
  // React StrictMode double-mounts: mount→cleanup→mount.
  // Without this guard the first cleanup kills the WS before it connects.
  useEffect(() => {
    if (transport === 'poll') return
    let active = true

    // Small delay lets StrictMode's immediate cleanup run before we open the socket
    const id = setTimeout(() => {
      if (active) connect()
    }, 0)

    return () => {
      active = false
      clearTimeout(id)

      if (reconnectTimerRef.current) {
        clearTimeout(reconnectTimerRef.current)
        reconnectTimerRef.current = null
      }
      if (wsRef.current) {
        wsRef.current.onclose = null // Prevent reconnect on intentional close
        wsRef.current.close()
        wsRef.current = null
      }
      setStatus('disconnected')
    }
  }, [connect, transport])

  // Desktop's authenticated engine transports hosted jobs over HTTP. Feed the
  // same event handler so results, placeholders and errors behave identically.
  useEffect(() => {
    if (transport !== 'poll' || !canvasId) return
    let cancelled = false, inFlight = false
    const poll = async () => {
      if (inFlight) return
      const jobs = [...useJobStore.getState().jobs.values()].filter(j => j.canvasId === canvasId && (j.status === 'PENDING' || j.status === 'RUNNING'))
      if (!jobs.length) return
      inFlight = true
      try {
        const result = await apiCall(`/api/jobs/canvas/${canvasId}?status=PENDING,RUNNING,COMPLETED,FAILED`, true) as { data?: { jobs?: { id: string; nodeId?: string; type: string; status: string; progress?: number; output?: unknown; error?: string; executionTime?: number }[] } }
        if (cancelled) return
        setStatus('connected')
        for (const job of result.data?.jobs ?? []) {
          const local = jobs.find(j => j.id === job.id)
          if (!local) continue
          const payload = { jobId: job.id, nodeId: local.nodeId, type: job.type, progress: job.progress ?? 0, output: job.output, error: job.error, executionTime: job.executionTime ?? 0, willRetry: false, retryCount: 0 }
          const type = job.status === 'COMPLETED' ? WS_EVENTS.GENERATION_COMPLETE : job.status === 'FAILED' ? WS_EVENTS.GENERATION_ERROR : job.status === 'RUNNING' && local.status === 'PENDING' ? WS_EVENTS.GENERATION_START : WS_EVENTS.GENERATION_PROGRESS
          handleMessageRef.current(new MessageEvent('message', { data: JSON.stringify({ type, payload }) }))
        }
      } catch { if (!cancelled) setStatus('error') }
      finally { inFlight = false }
    }
    const timer = setInterval(() => { void poll() }, 2000)
    void poll()
    return () => { cancelled = true; clearInterval(timer) }
  }, [canvasId, transport])

  return { status }
}

/**
 * After WebSocket reconnects, fetch current job statuses from API
 * and reconcile with local jobStore. This ensures jobs that completed
 * while disconnected are reflected in the UI.
 */
async function syncJobsAfterReconnect(canvasId: string): Promise<void> {
  const store = useJobStore.getState()
  const localJobs = Array.from(store.jobs.values()).filter(
    (j) => j.canvasId === canvasId && (j.status === 'PENDING' || j.status === 'RUNNING')
  )

  if (localJobs.length === 0) return

  try {
    const result = (await apiCall(`/api/jobs/canvas/${canvasId}?status=COMPLETED,FAILED`, true)) as {
      success: boolean
      data?: { jobs?: Array<{ id: string; status: string; output?: unknown; error?: string }> }
    }

    const serverJobs = result.data?.jobs ?? []
    const serverJobMap = new Map(serverJobs.map((j) => [j.id, j]))

    for (const localJob of localJobs) {
      const serverJob = serverJobMap.get(localJob.id)
      if (!serverJob) continue
      if (!localJob.nodeId) continue

      if (serverJob.status === 'COMPLETED') {
        store.updateJobStatus(localJob.id, 'COMPLETED', { output: serverJob.output, progress: 100 })
        // Unlock parent node
        const bridge = useCanvasBridgeStore.getState()
        bridge.updateNodeStateCallback?.(localJob.nodeId, { locked: false })
      } else if (serverJob.status === 'FAILED') {
        store.updateJobStatus(localJob.id, 'FAILED', { error: serverJob.error })
        const bridge = useCanvasBridgeStore.getState()
        bridge.updateNodeStateCallback?.(localJob.nodeId, { locked: false })
      }
    }
  } catch {
    // Non-critical — next WS message will catch up
  }
}
